/**
 * The deterministic fixed-timestep simulation orchestrator.
 *
 * Everything is a pure function of (level, variant, mirror, seed, speedProfile)
 * plus the stream of joystick commands supplied via setInput() before each
 * step(). The browser shell and the headless test harness drive this
 * identically.
 *
 * Control pipeline per step: raw input -> latency FIFO -> input smoothing ->
 * steering assistance (slide along static geometry; never auto-dodges the
 * pedestrian) -> safety governor -> clamped unicycle kinematics.
 * A stuck-detection watchdog triggers automatic recovery: first a short
 * reverse + re-align, then (if consecutively stuck) a safe-pose restore with
 * a small recorded time penalty. See docs/TECHNICAL_ARCHITECTURE.md.
 */

import { clamp, wrapAngle } from "../core/vec";
import {
  ASSIST_CLEAR_DIST,
  ASSIST_OFFSETS,
  ASSIST_OFFSETS_MISSION,
  COMMIT_OFFSET,
  CORNER_GAIN,
  CORNER_LOOKAHEAD_S,
  CORNER_MIN_SCALE,
  CUE_GAP,
  DT,
  JOYSTICK_DEADZONE,
  FRAME_FLIP_ANGLE,
  LATCH_ANGLE,
  LATCH_RELEASE_ANGLE,
  LATERAL_INPUT_THRESHOLD,
  MAX_COMMAND_HEADING,
  OSCILLATION_HOLD_S,
  STEER_SLEW_PER_S,
  OSCILLATION_WINDOW_S,
  MISSION_CUE_TIME_S,
  MISSION_WINDOW_END_TIME_S,
  PED_WALK_SPEED,
  RECOVERY_CONSECUTIVE_PROGRESS,
  RECOVERY_REVERSE_LENGTHS,
  RECOVERY_REVERSE_SPEED,
  SAFE_POSE_HEADING,
  SAFE_POSE_INTERVAL_S,
  SAFE_POSE_PED_DIST,
  SAFE_POSE_PENALTY_S,
  SAFE_POSE_STATIC_MARGIN,
  THROTTLE_MIN,
  SAMPLE_EVERY_STEPS,
  STATIC_MARGIN,
  STUCK_PED_NEAR_DIST,
  STUCK_PROGRESS_FRACTION,
  CORRIDOR_FLOOR_FRAC,
  LANE_COMBO_GRACE_S,
  LANE_STAR_EVERY_M,
  STUCK_SPEED_FRACTION,
  STUCK_WINDOW_S,
  TIMEOUT_S,
  WINDOW_END_GAP,
  YAW_ACCEL,
  YAW_GAIN,
  YIELD_SPEED,
  CHASSIS_SPRING,
  CLIMB_DRAG,
  WHEEL_HALF_BASE_M,
  WHEEL_HALF_TRACK_M,
  KERB_MOUNT_ANGLE_SIN,
  KERB_MOUNT_MIN_MPS,
  KERB_MOUNT_SPEED_LOSS,
  PAVEMENT_REACH_M,
  FALL_IN_MIN_SPEED,
  ADRIFT_WARN_S,
  WRONG_SIDE_MARGIN_M,
  WRONG_SIDE_WARN_S,
  NEARSIDE_MARCH_M,
  NEARSIDE_MARCH_STEP,
  FALL_SETTLE_S,
  RIDE_OVER_HEIGHT_M,
  RIDE_OVER_SPEED_LOSS,
} from "./constants";
import { ResearchPedAgent, agentCircles, type AgentModel } from "./agents";
import { EncounterManager, type EncounterRecord } from "./encounters";
import { buildLevel, type LevelDef } from "./levels";
import { pieceForward, pieceSdf, pieceTangent } from "./network";
import type { PathFrame } from "./path";
import type { PedestrianModel } from "./pedestrian";
import { fnv1a32 } from "../core/hash";
import { effectiveProfile, resolveSpeedProfile, type EffectiveProfile } from "./profile";
import {
  InputSmoother,
  LatencyQueue,
  isEngaged,
  stepRobot,
  type CommandedTwist,
  type ControlIntent,
} from "./robot";
import { LAB_SAFETY, MISSION_SAFETY, REAL_SAFETY, evaluateSafety, type SafetyResult } from "./safety";
import { CorridorPlanner, guideAt, type CorridorPlan, type CorridorStation } from "./corridor";
import { buildLaneRibbon, ribbonToStations } from "./lane_ribbon";
import type { GuideEdges } from "./guide_geometry";
import type {
  AgentView,
  AssistMode,
  ControlScheme,
  Guidance,
  JoystickCommand,
  MissionScore,
  Outcome,
  Phase,
  RecoveryMode,
  ResearchValidity,
  RobotProfile,
  RobotState,
  SafetyHazard,
  SafetyState,
  ScenarioConfig,
  Side,
  SimEvent,
  SimSample,
  SimView,
} from "./types";
import { NEUTRAL_COMMAND } from "./types";
import {
  atDestination,
  checkCollision,
  clampToPavement,
  clampToWalls,
  freeDistance,
  pitEntered,
  rideOverKind,
  surfaceHeightAt,
  robotShape,
  wallClearance,
  type RobotShape,
} from "./world";
import {
  KEEP_NEARSIDE_M,
  NEARSIDE_EXCUSE_M,
  RunMetricsCollector,
  kerbSide,
  type RunReport,
} from "./metrics";

const PHASE_ORDER: Record<Phase, number> = {
  approach: 0,
  cue: 1,
  negotiation: 2,
  completion: 3,
};

const STUCK_WINDOW_STEPS = Math.round(STUCK_WINDOW_S / DT);
const STUCK_COOLDOWN_S = 0.6;
/** Static blockage must be this close for a stall to count as stuck (m). */
const STUCK_FREE_DIST = 1.0;
/** Recovery align: candidate offsets around the destination bearing. */
const ALIGN_OFFSETS: readonly number[] = [
  0,
  Math.PI / 12, -Math.PI / 12,
  Math.PI / 6, -Math.PI / 6,
  Math.PI / 4, -Math.PI / 4,
  Math.PI / 3, -Math.PI / 3,
  Math.PI / 2, -Math.PI / 2,
];
const ALIGN_CLEAR_DIST = 1.5;
const ALIGN_DONE_RAD = 0.09;
/** Control-frame yaw follows the road direction with this time constant (s). */
const FRAME_TAU = 0.3;
/** Extra lead (s) on top of the control loop's own lag when sampling the
 * frame ahead of the robot (see updateFrame). */
const FRAME_LEAD_EXTRA_S = 0.05;
/** Turn guidance appears this far before the maneuver (m). */
const GUIDE_DIST = 28;
/** Control frame piece selection: a road counts as "on" within this distance
 * of its band (tolerates junction curb pockets) and "heading along" within
 * this angle of its direction. */
const FRAME_INSET = 0.35;
const FRAME_ALIGN = Math.PI / 3;
/** "Wrong way" = not on the road the route is on at this progress (a wrong
 * branch), or this far beyond the route's corridor along an adjacent road
 * (m), or driving against the route direction. The wrong LANE of the right
 * road is never wrong way. */
const OFF_ROUTE_FAR = 8.0;
/** Road pieces the route may legitimately be on at each path label. */
const ROUTE_PIECES: Record<string, readonly string[]> = {
  A1: ["A1"],
  J1: ["A1", "S1"],
  S1: ["S1"],
  R: ["R", "S1", "S2"],
  S2: ["S2", "R"],
  J2: ["S2", "A2"],
  A2: ["A2"],
};
const WRONG_WAY_LATCH_S = 0.6;
/** A heading-only "wrong way" must also have lost this much route progress. */
const WRONG_WAY_BACK_M = 1.5;
/** Driving this long without getting closer to the customer offers the
 * turn-around hint (overshooting a junction rarely takes you off the road). */
const NO_PROGRESS_S = 2.5;
const WRONG_WAY_CLEAR_S = 0.3;

interface Pose {
  x: number;
  z: number;
  heading: number;
}

export interface SimulationOptions {
  /** 1-based attempt number; attempts > 1 record a `retry` event. */
  attemptNumber?: number;
}

export class Simulation {
  readonly cfg: ScenarioConfig;
  /** UNVALIDATED base profile (config/robot_profile.json snapshot). */
  readonly robotProfile: RobotProfile;
  /** Base profile scaled by the selected speed profile. */
  readonly eff: EffectiveProfile;
  /** Catalogue case this run stages (ADR-075), or null. */
  get caseId(): string | null {
    return this.cfg.caseId ?? this.cfg.map?.case_id ?? null;
  }
  /**
   * Identity of the map actually played: a hash of the custom map document,
   * or "builtin" for the compiled-in district (ADR-075). Lets an episode be
   * matched to the exact map bytes it was recorded on.
   */
  get mapHash(): string {
    return this.cfg.map ? fnv1a32(JSON.stringify(this.cfg.map)) : "builtin";
  }
  readonly level: LevelDef;
  readonly dt = DT;
  readonly shape: RobotShape;

  t = 0;
  stepIndex = 0;
  robot: RobotState;
  pedModel: PedestrianModel | null;
  safetyState: SafetyState = "NORMAL";
  lastSafety: SafetyResult | null = null;
  phase: Phase = "approach";
  ended = false;
  outcome: Outcome | null = null;

  recoveryMode: RecoveryMode = "normal";
  recoveryCount = 0;
  penaltyTimeS = 0;
  readonly researchValidity: ResearchValidity | null;

  // Mission state (empty/inert for the lab levels).
  readonly agents: AgentModel[] = [];
  readonly encounterManager: EncounterManager | null = null;
  private readonly researchAgent: ResearchPedAgent | null = null;
  /** Control model actually in force this episode (ADR-023). */
  readonly controlScheme: ControlScheme;
  /** Robot's delivery-path frame (mission); identity (s = z, d = x) on lab levels. */
  frame: PathFrame;
  /** Control-frame yaw: joystick "up" drives along this heading (0 on lab levels). */
  frameYaw = 0;
  /** True while a pivot holds the frame still (renderer holds the camera too). */
  frameFrozen = false;
  private currentPiece: number | null = null;
  /** Direction sign of the current band piece's frame (+1 = polyline order). */
  private frameSign: 1 | -1 = 1;
  /** Latched world heading for a held sideways stick (mission), or null. */
  private latchedHeading: number | null = null;
  private latchStickX = 0;
  private latchStickY = 0;
  private lastPivotDir: 1 | -1 = 1;
  /** Rate steering: the slew-limited wheel position in [-1, 1]. */
  private wheel = 0;
  /** Heading the current pivot rotates to (null before one starts). */
  private pivotTarget: number | null = null;
  /** True once the pivot has reached its target: the frame (and so the camera)
   * is released to catch up while the player is still holding the stick. */
  private pivotSettled = false;
  /** True while a pivot (rotate-in-place) command is active: the frame and
   * camera hold still so the player sees the robot turn, not the world. */
  private pivoting = false;
  guidance: Guidance | null = null;
  private wrongWay = false;
  private wrongWayTimer = 0;
  private wrongWayCount = 0;
  private shownTurnS: number | null = null;
  /** Path progress when the current wrong-way suspicion began. */
  private prevProgressS = 0;
  /** Closest the robot has come to the customer, and how long it has been
   * driving without beating it: overshooting a junction usually leaves you on
   * a perfectly legal road, so "off the route" never fires — but you are
   * still going nowhere, and that is what the player needs telling. */
  private bestToGo = Infinity;
  private noProgressS = 0;
  /** True when the player should turn around (lost, or simply not getting
   * closer to the customer). Drives both the guidance and the guided pivot. */
  needsTurnAround = false;
  /** Lateral distance to the route last step, and whether it is shrinking:
   * a player driving back toward the road is doing the right thing and must
   * not be shouted at for it. */
  private prevAbsD = Infinity;
  private closingOnRoute = false;
  /** Research cue/window gaps (m): fixed on the lab level, closing-time based on the mission. */
  private readonly cueGap: number;
  private readonly windowEndGap: number;
  private boostUntil = 0;
  packageCondition = 100;
  private abruptCount = 0;
  private collisionCount = 0;
  private estopCount = 0;
  private lastEstopT = -Infinity;
  private slowdownCount = 0;
  private reversalCount = 0;
  /** Dithering: sustained lateral pushes that flip sign quickly (input-based,
   * curve-proof) — the scored quantity. */
  private oscillationCount = 0;
  private lastPushSide: Side | null = null;
  private lastPushT = -Infinity;
  private pushHoldS = 0;
  private pushSide: Side | null = null;
  private collisionCooldownUntil = 0;
  /** Collisions since the robot last made real progress, and where that was:
   * a player holding the stick into a wall must not bounce off it forever. */
  private collisionsHere = 0;
  private collisionsHereS = -Infinity;
  private encounterHadCollision = false;
  score: MissionScore | null = null;
  /**
   * The end-of-run report: counted events with times and places, plus ratios
   * whose numerator and denominator are both stated. This is what the end
   * panel shows; `score` is kept for the telemetry schema and the lab pins.
   */
  report: RunReport | null = null;
  private readonly metrics = new RunMetricsCollector();
  /** Driving assistance in force this attempt (ScenarioConfig.assist). */
  readonly assistMode: AssistMode;
  /** Kerb clearance measured before the wall clamp ran, for the metrics. */
  private preClampClearance = Infinity;
  /** Is the robot's body actually past the kerb right now (post-clamp)? Only
   * ever true under the real-physics model, which lets it up there. */
  private onPavement = false;
  /** Which low obstacle the robot is crossing, so the jolt lands on the
   * rising edge rather than on every step it spends over one. */
  private ridingOver: string | null = null;
  /** Seconds spent out from the kerb with a clear road, for the live warning. */
  private adriftFor = 0;
  private wrongSideFor = 0;
  /** Live lane state for the HUD gauge (ADR-052). */
  lane: { nearside: number; wrongSide: boolean; clearAhead: boolean } = {
    nearside: Infinity,
    wrongSide: false,
    clearAhead: false,
  };
  /**
   * How the body is sitting on the ground right now (ADR-058): metres above
   * the carriageway, and the pitch/roll the wheel heights imply. Public so
   * the renderer can pose the chassis instead of drawing it flat.
   */
  chassis = { height: 0, pitch: 0, roll: 0 };
  /**
   * The hole the robot has gone into, once it has. Public so the renderer can
   * tip the camera the way the robot tipped (ADR-043).
   */
  inPit: { kind: string; roll: "left" | "right" | null; t: number } | null = null;
  /** True while part of the body is over the kerb, so one climb counts once. */
  private overKerb = false;
  /** Transient on-screen warning, cleared by the HUD after it is shown. */
  warning: { text: string; t: number } | null = null;

  // --- Living Safe Corridor (ADR-065); inert on non-corridor levels. -----
  private corridorPlanner: CorridorPlanner | null = null;
  /** The current plan, recomputed every step (null on lab levels). */
  corridor: CorridorPlan | null = null;
  /** The stationary painted guidance lane (amendment C), or null. */
  readonly corridorGuide: CorridorStation[] | null = null;
  /** The same lane as WORLD-space edge polylines — what the renderer draws
   * and what the geometry tests check (amendment J). */
  readonly corridorRibbon: GuideEdges | null = null;
  private lastShortenT = -Infinity;
  private lastPocketEventT = -Infinity;
  /** Lane reward (amendment E): stars earned by driving IN the painted
   * lane, with a combo that grows while the streak holds. Pure feedback —
   * it never touches the mission score or the research instrument, and it
   * pauses (without resetting) through the research-neutral window. */
  laneStars = 0;
  laneCombo = 0;
  laneComboBest = 0;
  laneScore = 0;
  private laneDistM = 0;
  private offLaneS = 0;
  private wasInLane = false;
  /** True while the SYSTEM (corridor or governor), not the player, is the
   * reason the robot is slower than commanded — drives the underglow. */
  systemSlowed = false;
  private pocketEntered = false;
  /** The robot actually stood still during the amber — a real yield. */
  private stoppedInYield = false;
  private prematureFired = false;
  private cleanYield = false;
  private resumeArmed = false;
  private pushAtMouthS = 0;

  readonly events: SimEvent[] = [];
  readonly samples: SimSample[] = [];
  /** The commanded Twist of the most recent step (ADR-075). */
  lastCmd: CommandedTwist = { v: 0, w: 0 };
  /**
   * A Twist from an external driver (ADR-076), or null for the joystick.
   * While set it replaces the stick's target speed and yaw rate at the plant;
   * the safety governor, the corridor and the kerb all still apply.
   */
  private externalCmd: CommandedTwist | null = null;

  // Latches (per attempt).
  private cueT: number | null = null;
  private reactive = false;
  private committedSide: Side | null = null;
  private lastSideAtCommitDepth: Side | null = null;
  private firstInputSeen = false;
  private firstLateralSeen = false;
  private prevEngagedRaw = false;
  private crossed = false;
  private passingT: number | null = null;
  private passResult: "pass_left" | "pass_right" | "yield" | null = null;
  private boostActive = false;

  private readonly latency: LatencyQueue;
  private readonly smoother: InputSmoother;
  private rawCmd: JoystickCommand = NEUTRAL_COMMAND;
  private lastPivotOnly = false;

  // Recovery bookkeeping.
  private lastSafePose: Pose;
  private lastSafePoseT = -Infinity;
  private zHistory: number[] = [];
  private zAtLastRecoveryEnd: number | null = null;
  private stuckCooldownUntil = 0;
  private reverseRemaining = 0;
  private alignTarget = 0;

  constructor(cfg: ScenarioConfig, profile: RobotProfile, opts: SimulationOptions = {}) {
    const profileName = resolveSpeedProfile(cfg.speedProfile, cfg.level);
    this.cfg = { ...cfg, speedProfile: profileName };
    this.robotProfile = profile;
    this.eff = effectiveProfile(profile, profileName, cfg.tuning);
    this.cfg.tuning = this.eff.tuning;
    this.level = buildLevel(cfg);
    this.shape = robotShape(profile);
    this.robot = { ...this.level.robotSpawn, speed: 0, yawRate: 0 };
    this.lastSafePose = { ...this.level.robotSpawn };
    this.pedModel = this.level.makePedestrian();
    this.latency = new LatencyQueue(this.eff);
    this.smoother = new InputSmoother(this.eff.input_smoothing_tau_s);
    // The level's model unless the episode asks for the other one (so the
    // two can be compared on the same map; recorded in the episode).
    this.controlScheme =
      cfg.controlScheme === "heading" || cfg.controlScheme === "rate"
        ? cfg.controlScheme
        : this.level.features.steering;
    this.cfg.controlScheme = this.controlScheme;
    // The lab levels are the pinned research instrument (ADR-007): their
    // trajectories are hashed in tests/unit/lab_pins.test.ts and the
    // two-stage governor is part of what is being measured, so they keep the
    // assisted model unless an episode explicitly asks otherwise. Everything
    // else — the mission, custom maps — defaults to real physics.
    this.assistMode =
      cfg.assist ?? (this.level.features.missionSafety ? "real" : "assisted");
    this.cfg.assist = this.assistMode;
    // Real physics: low things on the road surface are crossed rather than
    // hit (ADR-038). The assisted model leaves every obstacle solid, which is
    // what the lab levels and the pinned trajectories were recorded against.
    this.level.world.rideOverBelow = this.assistMode === "real" ? RIDE_OVER_HEIGHT_M : 0;
    this.frame = this.identityFrame();
    if (this.level.mission) {
      this.agents = this.level.mission.makeAgents();
      this.encounterManager = new EncounterManager(
        this.level.mission.encounters,
        this.agents,
        this.shape.r,
      );
      this.researchAgent =
        (this.agents.find((a) => a.state.type === "research_ped") as ResearchPedAgent) ?? null;
      this.frame = this.level.mission.path.project(this.robot.x, this.robot.z, 0);
      this.frameYaw = this.frame.theta;
      this.currentPiece = this.level.mission.network.frameAt(
        this.robot.x,
        this.robot.z,
        this.robot.heading,
        null,
      ).index;
      // Closing-time protocol (ADR-022): the robot's pace is tunable over a
      // 6x range, so the stimulus is scheduled in seconds, not meters.
      const closing = this.eff.cruise_mps + PED_WALK_SPEED;
      this.cueGap = MISSION_CUE_TIME_S * closing;
      this.windowEndGap = MISSION_WINDOW_END_TIME_S * closing;
      this.guidance = this.computeGuidance();
      if (this.cfg.corridor ?? this.level.features.corridor) {
        const ribbon = buildLaneRibbon(
          this.level.mission.path,
          this.level.mission.network,
          this.shape,
          this.level.world.crossings,
        );
        this.corridorRibbon = ribbon;
        this.corridorGuide = ribbonToStations(this.level.mission.path, ribbon);
      }
    } else {
      this.cueGap = CUE_GAP;
      this.windowEndGap = WINDOW_END_GAP;
    }
    // The lab research level AND the mission (whose section 2 is the primary
    // research encounter) both carry validity bookkeeping.
    this.researchValidity =
      this.level.isResearch || this.level.mission
        ? { sample_valid: true, invalid_reason: null, post_commit_recovery: false }
        : null;

    this.emit("level_start", {
      level: cfg.level,
      variant: cfg.variant,
      mirror: cfg.mirror ? 1 : 0,
      seed: cfg.seed,
      speed_profile: this.eff.speed_profile,
      tuning: { ...this.eff.tuning },
      control_scheme: this.controlScheme,
      attempt_number: opts.attemptNumber ?? 1,
      map_id: this.level.mission?.mapId ?? null,
      cue_gap_m: round3(this.cueGap),
    });
    if ((opts.attemptNumber ?? 1) > 1) {
      this.emit("retry", { attempt_number: opts.attemptNumber });
    }
  }

  /**
   * Hand the plant to an external driver (ADR-076): `v` m/s forward, `w`
   * rad/s yaw rate in the SIM's convention — positive = turning right; ROS
   * `angular.z` is the opposite sign. Pass null to give the stick back. Keep
   * feeding a joystick command (setInput) meanwhile, so the run's engagement
   * logic sees a driver at the controls. Non-finite values read as zero.
   */
  setExternalTwist(cmd: CommandedTwist | null): void {
    const num = (x: number): number => (Number.isFinite(x) ? x : 0);
    this.externalCmd = cmd === null ? null : { v: num(cmd.v), w: num(cmd.w) };
  }

  /** Supply the player's raw joystick command for the upcoming step(s). */
  setInput(cmd: JoystickCommand): void {
    const len = Math.hypot(cmd.dirX, cmd.dirY);
    const mag = clamp(cmd.mag, 0, 1);
    if (len < 1e-6 || mag <= 0) {
      this.rawCmd = { dirX: 0, dirY: 0, mag: 0 };
    } else {
      this.rawCmd = { dirX: cmd.dirX / len, dirY: cmd.dirY / len, mag };
    }
  }

  /** The mission's research-encounter config (single source of truth for the
   * zone bounds and record id — never hardcode them elsewhere). */
  private researchCfg() {
    return this.encounterManager?.configs.find((c) => c.research) ?? null;
  }

  /** The research pedestrian's state: lab pedModel or the mission agent. */
  private researchPedState() {
    if (this.pedModel) return this.pedModel.state;
    const a = this.researchAgent?.state;
    if (!a || !a.active) return null;
    return {
      x: a.x,
      z: a.z,
      vx: a.vx,
      vz: a.vz,
      safetyVx: a.safetyVx,
      safetyVz: a.safetyVz,
      active: true,
    };
  }

  /** Active collidable dynamic hazards for safety/collision (one per body circle). */
  private hazards(): SafetyHazard[] {
    if (this.agents.length > 0) {
      const out: SafetyHazard[] = [];
      for (const a of this.agents) {
        const s = a.state;
        if (!s.active || !s.collidable) continue;
        for (const c of agentCircles(s)) {
          out.push({
            id: s.id,
            x: c.x,
            z: c.z,
            safetyVx: s.safetyVx,
            safetyVz: s.safetyVz,
            radius: s.radius,
          });
        }
      }
      return out;
    }
    const ped = this.pedModel?.state;
    return ped && ped.active
      ? [{ id: "pedestrian", x: ped.x, z: ped.z, safetyVx: ped.safetyVx, safetyVz: ped.safetyVz, radius: 0.26 }]
      : [];
  }

  private identityFrame(): PathFrame {
    return {
      s: this.robot.z,
      d: this.robot.x,
      theta: 0,
      halfWidth: this.level.world.alleyHalfWidth,
      piece: "",
    };
  }

  /** Progress along the delivery path (mission) or z (lab levels). */
  private progress(): number {
    return this.level.world.path ? this.frame.s : this.robot.z;
  }

  /** Lateral offset from the delivery path, +right (plain x on lab levels). */
  private offsetX(): number {
    return this.level.world.path ? this.frame.d : this.robot.x;
  }

  /** Re-project the robot into the path frame and ease the control frame
   * toward the current road piece's direction (mission only). */
  private updateFrame(snap = false): void {
    const world = this.level.world;
    if (!world.path || !world.network) {
      this.frame = this.identityFrame();
      return;
    }
    this.frame = world.path.project(this.robot.x, this.robot.z, this.frame.s);
    // While pivoting the yaw frame holds still (the robot turns on screen,
    // the world does not); the flip logic runs again once the stick returns.
    this.frameFrozen = this.pivoting && !this.pivotSettled;
    if (this.frameFrozen && !snap) return;
    // The frame is sampled AHEAD of the robot by its own control lag: command
    // latency + input smoothing + the proportional loop's tracking lag
    // (1 / steer gain). On a curve a P loop tracking a rotating target sits
    // omega x lag behind it, so without this lead "straight" spirals outward.
    const lagS =
      this.eff.control_latency_ms / 1000 + this.eff.input_smoothing_tau_s + 1 / this.eff.steer_gain;
    const lead = Math.max(0, this.robot.speed) * (lagS + FRAME_LEAD_EXTRA_S);
    const qx = this.robot.x + Math.sin(this.robot.heading) * lead;
    const qz = this.robot.z + Math.cos(this.robot.heading) * lead;
    // The led point only counts while it is on (or within the tolerance of) the road.
    const usable = world.network.sdf(qx, qz) < FRAME_INSET;
    // Frame piece = the road the robot is ON (within FRAME_INSET of its
    // band, at the robot or at the led point) AND HEADING ALONG (within
    // FRAME_ALIGN of its direction, either sign for two-way bands). While no
    // piece satisfies both — mid-swing in a junction, stopped in a curb
    // pocket, pivoting — the frame keeps its piece. The current piece wins
    // unless another is clearly better aligned.
    const net = world.network;
    const heading = this.robot.heading;
    let bestIdx = this.currentPiece;
    let bestScore = Infinity;
    for (let i = 0; i < net.pieces.length; i++) {
      const p = net.pieces[i]!;
      if (p.kind === "band" && p.frame === false) continue;
      const dHere = pieceSdf(p, this.robot.x, this.robot.z);
      const dLed = usable ? pieceSdf(p, qx, qz) : Infinity;
      if (Math.min(dHere, dLed) > FRAME_INSET) continue;
      const px = dLed <= dHere ? qx : this.robot.x;
      const pz = dLed <= dHere ? qz : this.robot.z;
      const align = Math.abs(wrapAngle(pieceTangent(p, px, pz, heading) - heading));
      if (align > FRAME_ALIGN) continue;
      let score = align;
      if (i === this.currentPiece) score -= 0.5;
      if (score < bestScore) {
        bestScore = score;
        bestIdx = i;
      }
    }
    if (bestIdx === null) bestIdx = net.deepestPiece(this.robot.x, this.robot.z);
    const pf = {
      index: bestIdx,
      piece: net.pieces[bestIdx]!,
      sdf: pieceSdf(net.pieces[bestIdx]!, this.robot.x, this.robot.z),
      tangent: 0,
    };
    const changed = pf.index !== this.currentPiece;
    this.currentPiece = pf.index;

    // Direction sign with hysteresis: a band's frame only flips once the
    // robot's heading is FRAME_FLIP_ANGLE away from it (a deliberate U-turn),
    // never at the 90 deg tie a transient junction swing passes through.
    let tangent: number;
    if (pf.piece.kind === "ring") {
      tangent = pieceForward(pf.piece, usable ? qx : this.robot.x, usable ? qz : this.robot.z);
    } else {
      const px = usable ? qx : this.robot.x;
      const pz = usable ? qz : this.robot.z;
      const fwd = pieceForward(pf.piece, px, pz);
      if (changed || snap) {
        this.frameSign = Math.abs(wrapAngle(fwd - this.robot.heading)) <= Math.PI / 2 ? 1 : -1;
      } else {
        const current = this.frameSign > 0 ? fwd : wrapAngle(fwd + Math.PI);
        if (Math.abs(wrapAngle(this.robot.heading - current)) > FRAME_FLIP_ANGLE) {
          this.frameSign = this.frameSign > 0 ? -1 : 1;
        }
      }
      tangent = this.frameSign > 0 ? fwd : wrapAngle(fwd + Math.PI);
    }

    if (snap) {
      this.frameYaw = tangent;
      return;
    }
    const alpha = 1 - Math.exp(-DT / FRAME_TAU);
    let delta = wrapAngle(tangent - this.frameYaw);
    // A near-180 deg target (a flip) eases around in the direction the robot
    // actually turned, so the camera and command frame follow the robot
    // instead of swinging the other way across the seam.
    if (Math.abs(delta) > (165 * Math.PI) / 180) {
      const dir = wrapAngle(this.robot.heading - this.frameYaw) >= 0 ? 1 : -1;
      delta = dir * Math.abs(delta);
    }
    this.frameYaw = wrapAngle(this.frameYaw + delta * alpha);
  }

  /** Clamp a frame-relative command heading and lift it to world space. */
  private worldHeading(rel: number): number {
    return wrapAngle(this.frameYaw + clamp(rel, -MAX_COMMAND_HEADING, MAX_COMMAND_HEADING));
  }

  /** Read-only view for scripted players / debug overlays. */
  view(): SimView {
    const world = this.level.world;
    const agentViews: AgentView[] = [];
    for (const a of this.agents) {
      const s = a.state;
      if (s.active && s.collidable) {
        agentViews.push({
          id: s.id,
          type: s.type,
          x: s.x,
          z: s.z,
          vx: s.vx,
          vz: s.vz,
          radius: s.radius,
          moving: s.moving,
          heading: s.heading,
        });
      }
    }
    const researchPed = this.researchPedState();
    return {
      t: this.t,
      phase: this.phase,
      robot: this.robot,
      ped: this.pedModel?.state ?? (researchPed as SimView["ped"]),
      cueOnsetSeen: this.cueT !== null,
      pedDriftSide:
        this.cueT !== null
          ? (this.pedModel?.driftSide() ?? this.researchAgent?.driftSide() ?? null)
          : null,
      destination: world.destination,
      recoveryCount: this.recoveryCount,
      agents: agentViews,
      // Holes are NOT in the perception list under the real model (ADR-043).
      //
      // `view.obstacles` is what a driver — scripted or otherwise — plans
      // against, and a hole in the road is the textbook thing a delivery
      // robot's obstacle detection misses: a negative obstacle, below the
      // ground plane, invisible to a sensor looking for things standing on it.
      // Leaving them in made the scripted teacher steer neatly around every
      // pothole, which is the opposite of the case worth capturing.
      // Two exclusions, for opposite reasons. A PIT is invisible to the real
      // model because its sensors cannot see below the road plane (ADR-043).
      // A BUMP is excluded in every mode because it is not an obstacle at all:
      // the planner never routes around a speed bump, it drives over it — and
      // leaving bumps in this list had the scripted teacher politely swerving
      // around every one, which is not what anyone does at a speed bump.
      obstacles: world.obstacles.filter(
        (o) => !o.bump && !(o.pit && (world.rideOverBelow ?? 0) > 0),
      ),
      path: world.path ?? null,
      network: world.network ?? null,
      progress: { s: this.progress(), d: this.offsetX() },
      frameYaw: this.frameYaw,
      controlScheme: this.controlScheme,
      guidance: this.guidance,
      corridor: this.corridor,
      distanceToGo: world.path
        ? Math.max(0, world.path.length - this.frame.s)
        : Math.max(0, world.destination.z - this.robot.z),
    };
  }

  /** Advance exactly one fixed timestep. */
  step(): void {
    if (this.ended) return;

    const previousPose = { x: this.robot.x, z: this.robot.z, heading: this.robot.heading };

    // 1) Telemetry sample of the pre-step state (includes t = 0).
    if (this.stepIndex % SAMPLE_EVERY_STEPS === 0) {
      this.samples.push(this.makeSample());
    }

    // 2) Player-behavior events from the RAW input stream (pre-latency).
    this.processRawInputEvents();

    // 3) Delayed command through the control-latency FIFO (always fed, so
    //    latency state stays consistent across recovery maneuvers).
    const delayed = this.latency.push(this.rawCmd);

    if (this.recoveryMode !== "normal") {
      this.recoveryStep();
      this.blockTreeMotion(previousPose);
      this.updateFrame();
      this.stepActors();
      this.detect();
      this.t += this.dt;
      this.stepIndex += 1;
      return;
    }

    // 4) Input smoothing (absorbs small accidental joystick movements).
    const cmd = this.smoother.update(delayed);
    const engaged = isEngaged(cmd);

    // 5) Steering assistance: slide along static geometry toward free space.
    //    Commands are relative to the CONTROL FRAME (the road direction on
    //    the mission, +z on lab levels): "up" follows the road.
    let heading = this.robot.heading;
    let pivotOnly = false;
    let pivotDir: 0 | 1 | -1 = 0;
    const rate = this.controlScheme === "rate";
    const frameControl = this.level.features.frameControl && !rate;
    if (rate) {
      // Racing model: the raw axes ARE the controls. Nothing to assist, no
      // frame to fight, and no special gesture for turning round.
      this.latchedHeading = null;
      this.pivoting = false;
      this.pivotTarget = null;
      this.pivotSettled = false;
    }
    if (engaged) {
      const rawRel = Math.atan2(cmd.dirX, cmd.dirY);
      pivotOnly = Math.abs(rawRel) > MAX_COMMAND_HEADING;
      if (pivotOnly && frameControl) {
        // Pull-back = "turn me around": rotate in place to the way out and
        // STOP there (amendment M). The target is chosen once per pivot.
        if (!this.pivoting) {
          this.pivotSettled = false;
          // A pull-back always means "turn me around", and always stops when
          // it is facing the way back: overshooting a junction leaves you on a
          // legal road, so gating this on the wrong-way flag left exactly the
          // case players hit most with the old endless spin.
          const target = this.escapeHeading();
          const turn = target === null ? 0 : wrapAngle(target - this.robot.heading);
          this.lastPivotDir =
            Math.abs(cmd.dirX) > 0.35 ? (cmd.dirX > 0 ? 1 : -1) : turn >= 0 ? 1 : -1;
          this.pivotTarget = target;
        }
        pivotDir = this.lastPivotDir;
        this.latchedHeading = null;
        this.pivoting = true;
        if (
          this.pivotTarget !== null &&
          Math.abs(wrapAngle(this.pivotTarget - this.robot.heading)) < 0.15
        ) {
          this.pivotSettled = true;
        }
      } else if (pivotOnly) {
        heading = this.worldHeading(rawRel);
      } else if (frameControl && Math.abs(rawRel) >= LATCH_ANGLE) {
        // Sideways push: latched as a WORLD heading until the stick moves —
        // one push is one turn, whatever the frame does meanwhile.
        const moved =
          this.latchedHeading === null ||
          Math.abs(wrapAngle(Math.atan2(cmd.dirX, cmd.dirY) - Math.atan2(this.latchStickX, this.latchStickY))) >
            LATCH_RELEASE_ANGLE;
        if (moved) {
          this.latchedHeading = this.worldHeading(rawRel);
          this.latchStickX = cmd.dirX;
          this.latchStickY = cmd.dirY;
        }
        heading = this.assistWorld(this.latchedHeading!);
      } else {
        this.latchedHeading = null;
        heading = this.assist(rawRel);
      }
    } else {
      this.latchedHeading = null;
    }
    if (pivotDir === 0) {
      this.pivoting = false;
      this.pivotTarget = null;
      this.pivotSettled = false;
    }
    this.lastPivotOnly = engaged && pivotOnly;

    // 5.5) Living Safe Corridor: recompute the plan for this step. The
    // governor stays the last word — the corridor only ever LOWERS the cap.
    // Free roam has no corridor: it is a lane discipline for a delivery, and
    // it caps the speed of a driver who has simply turned down a side street.
    if (!this.freeRoam) this.updateCorridor();

    // 6) Safety governor along the intended travel heading.
    const safety = evaluateSafety(
      this.level.world,
      this.robot,
      this.shape,
      this.hazards(),
      this.eff,
      this.safetyState,
      engaged ? heading : this.robot.heading,
      this.safetyOptions(),
    );
    if (safety.state !== this.safetyState) {
      if (safety.state === "SLOWDOWN" && this.safetyState === "NORMAL") {
        this.slowdownCount += 1;
        this.encounterManager?.bump("safety_slowdowns");
        this.emit("safety_slowdown", { ttc: finite(safety.ttc), free_dist: safety.freeDist });
      } else if (safety.state === "EMERGENCY_STOP") {
        // Every transition is an event (raw data); metrics count stop
        // EPISODES — re-entries within 1 s of the previous stop are the same
        // episode (a hazard inching past a halted robot must not read as ten).
        const episode = this.t - this.lastEstopT >= 1.0;
        this.lastEstopT = this.t;
        if (episode) {
          this.estopCount += 1;
          this.packageCondition = Math.max(0, this.packageCondition - 4);
          this.encounterManager?.bump("emergency_stops");
        }
        this.emit("emergency_stop", { ttc: finite(safety.ttc), free_dist: safety.freeDist, episode });
      }
    }
    this.safetyState = safety.state;
    this.lastSafety = safety;

    // 7) Kinematics under the effective (speed-profile-scaled) limits.
    const rawSteer = rate ? clamp(this.rawCmd.dirX * this.rawCmd.mag, -1, 1) : 0;
    const rawDrive = rate ? clamp(this.rawCmd.dirY * this.rawCmd.mag, -1, 1) : 0;
    // The wheel itself has inertia: slew-limit it so a flicked stick does not
    // snap the robot sideways.
    if (rate) {
      const maxStep = STEER_SLEW_PER_S * DT;
      this.wheel += clamp(rawSteer - this.wheel, -maxStep, maxStep);
    } else {
      this.wheel = 0;
    }
    const intent: ControlIntent = {
      engaged,
      steer: rate ? this.wheel : null,
      drive: rawDrive,
      heading,
      frameYaw: this.frameYaw,
      cornerScale: this.cornerScale(),
      throttle: this.throttle(cmd.mag),
      pivotOnly,
      pivotDir,
      pivotTarget: this.pivotTarget,
      yawBrake: frameControl,
      cruise: this.isBoosting() ? this.eff.boost_mps : this.eff.cruise_mps,
    };
    const corridorCap = this.corridor === null ? Infinity : this.corridor.speedCap;
    const capMin = Math.min(safety.speedCap, corridorCap);
    // What the PLAYER is asking for, against what the system grants: the
    // difference is shown as a calm underglow, so an automatic slow-down
    // never reads as broken controls. Suppressed through the research
    // window — a cue there would alter the measured encounter.
    const commanded =
      intent.cruise *
      (rate ? Math.max(0, rawDrive) : intent.throttle) *
      intent.cornerScale;
    // Meaningful holds only: the cap must sit well under the command AND the
    // robot must actually be riding it — a glow for every mild bend easing
    // would burn half the run and mean nothing.
    this.systemSlowed =
      engaged &&
      this.corridor !== null &&
      !this.researchNeutral() &&
      commanded > 1 &&
      capMin < commanded - 1.2 &&
      this.robot.speed <= capMin + 0.3;
    // Whether the robot was on the road at the START of this step, taken from
    // the post-clamp state of the last one. Reading `preClampClearance` here
    // instead is wrong in a way that quietly disables the kerb: that field
    // holds the clearance BEFORE the clamp pushed the robot back inside, so
    // one deflected brush leaves it negative, `wasInside` reads false for the
    // next step, and neither the mount test nor the clamp runs again for the
    // rest of the run — the robot then drifts out to the pavement's edge from
    // any angle at all.
    const wasInside = !this.level.world.network || !this.onPavement;
    this.lastCmd = stepRobot(this.robot, intent, capMin, this.eff, this.externalCmd ?? undefined);
    // Measure the kerb BEFORE the clamp slides the body back inside: after
    // the clamp the clearance always reads >= 0, and a robot that drove over
    // the kerb would look like one that never left the road.
    this.preClampClearance = this.level.world.network
      ? wallClearance(this.level.world, this.robot, this.shape)
      : Infinity;
    if (this.freeRoam) {
      // Free roam over a reconstruction is not a delivery down a modelled road
      // (ADR-128). The map's network exists to place the route and the camera;
      // the WORLD is the photographs, which reach across the whole street and
      // well past the carriageway. Clamping to the network there fences the
      // driver inside a strip of a scene they came to look around, and the
      // fence is invisible because the scenery it belongs to is hidden. So the
      // clamps, the tree blocking and the fall test all stand down; the ground
      // height still follows the surface, because the robot must ride on
      // something.
      this.onPavement = false;
      this.rideSurface();
    } else if (this.assistMode === "real" && this.level.world.network) {
      // Crossing OUT over the kerb costs speed — that is the bump. Coming back
      // down is free, as it is in life.
      if (wasInside && this.preClampClearance < 0 && this.crossedKerbFast()) {
        this.robot.speed *= 1 - KERB_MOUNT_SPEED_LOSS;
      } else if (wasInside && this.preClampClearance < 0) {
        // Not enough speed across it: the kerb turns the wheels aside instead.
        clampToWalls(this.level.world, this.robot, this.shape);
      }
      clampToPavement(this.level.world, this.robot, this.shape, PAVEMENT_REACH_M);
      this.onPavement = wallClearance(this.level.world, this.robot, this.shape) < 0;
      this.rideOverLowThings();
    this.rideSurface();
      this.checkForHoles();
    } else {
      clampToWalls(this.level.world, this.robot, this.shape);
      this.onPavement = false;
    }
    this.blockTreeMotion(previousPose);
    this.updateFrame();
    this.trackMetrics(safety);
    this.stepLaneReward();

    // 8) Pedestrian / mission agents.
    this.stepActors();

    // 9) Detections & episode logic.
    this.detect();

    // 10) Safe-pose tracking + stuck watchdog.
    if (!this.ended) {
      this.trackSafePose();
      this.watchForStuck(engaged);
    }

    this.t += this.dt;
    this.stepIndex += 1;
  }

  /**
   * Feed the metrics one step, and detect kerb climbs.
   *
   * A climb is the RISING EDGE of the body crossing the kerb line, not every
   * step it spends outside: grinding along a kerb for two seconds is one
   * mistake, and counting it 120 times would make the number meaningless.
   */
  private trackMetrics(safety: SafetyResult): void {
    const hasKerbs = this.level.world.network !== undefined;
    const step = {
      dt: this.dt,
      x: this.robot.x,
      z: this.robot.z,
      speed: this.robot.speed,
      yawRate: this.robot.yawRate,
      clearance: this.preClampClearance,
      hasKerbs,
      ttc: safety.ttc,
      nearside: this.nearsideDistance(),
      // Nothing worth leaving the kerb for: the sweep ahead is long and clear.
      clearAhead: safety.freeDist >= NEARSIDE_EXCUSE_M,
      wrongSide: this.onWrongSide(safety),
    };
    // For the HUD's live lane gauge; render-side reads, never writes.
    this.lane = { nearside: step.nearside, wrongSide: step.wrongSide, clearAhead: step.clearAhead };
    this.metrics.step(step);

    // Out in the middle of a clear road, and staying there. Warned live and
    // not merely counted, because the whole point is that it FEELS fine at the
    // time: an empty carriageway invites a slow robot into the middle of it,
    // and nothing pushes back until something comes the other way.
    if (hasKerbs) {
      const adrift = step.clearAhead && step.nearside > KEEP_NEARSIDE_M && this.robot.speed > 0.2;
      this.adriftFor = adrift ? this.adriftFor + this.dt : 0;
      if (this.adriftFor >= ADRIFT_WARN_S) {
        this.warn("Keep in to the kerb");
        // In the record as well as on the screen: each episode is one event,
        // so a run's discipline can be counted afterwards, not just felt.
        this.metrics.record("adrift", "off the kerb line", this.robot.x, this.robot.z, this.robot.speed, this.t);
        this.emit("adrift", { nearside_m: round3(step.nearside) });
        this.adriftFor = 0;
      }
      // The wrong half of a two-way road. Two seconds of grace covers the
      // lane change itself; staying there with a clear road is the offence.
      if (step.wrongSide) {
        this.wrongSideFor += this.dt;
        if (this.wrongSideFor >= WRONG_SIDE_WARN_S) {
          this.warn("Wrong side of the road");
          this.metrics.record("wrong_side", "left of the centre line", this.robot.x, this.robot.z, this.robot.speed, this.t);
          this.emit("wrong_side", { d: round3(this.frame.d) });
          this.wrongSideFor = 0;
        }
      } else {
        this.wrongSideFor = 0;
      }
    }

    if (!hasKerbs) return;
    const outside = this.preClampClearance < 0;
    if (outside && !this.overKerb) {
      const side = kerbSide(this.offsetX());
      this.metrics.record("kerb_climb", side, this.robot.x, this.robot.z, this.robot.speed, this.t);
      this.emit("kerb_climb", {
        side,
        overshoot_m: round3(-this.preClampClearance),
        speed: round3(this.robot.speed),
      });
      this.warn(`Kerb climb - ${side}`);
    }
    this.overKerb = outside;
  }

  /**
   * Cross a pothole, a patch of roadworks, a pallet — and pay for it.
   *
   * A fraction of speed, not a stop: driving over a hole in the road is a
   * mistake worth reporting, not a crash, and stopping the robot dead on one
   * is exactly the wrong-feeling behaviour this replaces. Applied on the
   * RISING EDGE, so a ladder lying across the road is one jolt and not sixty.
   */
  /**
   * Ride the surface (ADR-058).
   *
   * Four wheel contact points are sampled against `surfaceHeightAt`; the body
   * sits on their average and tilts by the differences. A suspension spring
   * smooths the result, so a 12 cm kerb is a lurch that settles rather than a
   * teleport, and a speed bump is a rise and a fall you can feel.
   *
   * Climbing costs speed continuously, in proportion to how fast the ground
   * is coming up under the wheels — the bump tax used to be one subtraction
   * at the rim, which meant crawling over a Gờ giảm tốc cost the same as
   * hitting it at cruise.
   */
  private rideSurface(): void {
    const world = this.level.world;
    const c = Math.cos(this.robot.heading);
    const s2 = Math.sin(this.robot.heading);
    // Wheel offsets in body frame: (+along = forward, +across = right).
    let front = 0;
    let rear = 0;
    let left = 0;
    let right = 0;
    for (const [along, across] of [
      [WHEEL_HALF_BASE_M, -WHEEL_HALF_TRACK_M],
      [WHEEL_HALF_BASE_M, WHEEL_HALF_TRACK_M],
      [-WHEEL_HALF_BASE_M, -WHEEL_HALF_TRACK_M],
      [-WHEEL_HALF_BASE_M, WHEEL_HALF_TRACK_M],
    ] as const) {
      const wx = this.robot.x + s2 * along + c * across;
      const wz = this.robot.z + c * along - s2 * across;
      const h = surfaceHeightAt(world, wx, wz);
      if (along > 0) front += h / 2;
      else rear += h / 2;
      if (across < 0) left += h / 2;
      else right += h / 2;
    }
    const targetH = (front + rear) / 2;
    const targetPitch = Math.atan2(rear - front, 2 * WHEEL_HALF_BASE_M);
    const targetRoll = Math.atan2(left - right, 2 * WHEEL_HALF_TRACK_M);
    // Critically-damped-ish approach; dt is fixed, so a plain lerp is enough.
    const k = Math.min(1, this.dt * CHASSIS_SPRING);
    const rise = targetH - this.chassis.height;
    this.chassis.height += rise * k;
    this.chassis.pitch += (targetPitch - this.chassis.pitch) * k;
    this.chassis.roll += (targetRoll - this.chassis.roll) * k;
    // Going UP costs momentum; coming down gives none of it back.
    if (rise > 1e-4 && this.robot.speed > 0.05) {
      this.robot.speed = Math.max(0, this.robot.speed - rise * CLIMB_DRAG * this.dt * 60);
    }
  }

  private rideOverLowThings(): void {
    const kind = rideOverKind(this.level.world, this.robot, this.shape);
    if (kind !== null && this.ridingOver === null) {
      this.robot.speed *= 1 - RIDE_OVER_SPEED_LOSS;
      this.metrics.record("rough_ride", kind, this.robot.x, this.robot.z, this.robot.speed, this.t);
      this.emit("rough_ride", { what: kind, speed: round3(this.robot.speed) });
      this.warn(`Drove over ${kind.replace(/_/g, " ")}`);
    }
    this.ridingOver = kind;
  }

  /**
   * Did a wheel go into a hole?
   *
   * Terminal, and deliberately so. A delivery robot with a wheel in a 0.9 m
   * pothole is not driving out of it, and pretending otherwise would make the
   * one case this exists to capture look survivable. The motion stops here and
   * stays stopped; the attempt ends on the clock or on Retry.
   */
  private checkForHoles(): void {
    if (this.inPit) {
      // Still in it. Nothing the stick does changes that.
      this.robot.speed = 0;
      this.robot.yawRate = 0;
      // Let it finish falling before the attempt is called, so the renderer
      // has the time it needs to tip the robot — and the cameras with it.
      if (!this.ended && this.t - this.inPit.t >= FALL_SETTLE_S) this.finish("incomplete");
      return;
    }
    if (this.robot.speed < FALL_IN_MIN_SPEED) return; // crept to the lip and stopped
    const pit = pitEntered(this.level.world, this.robot, this.shape);
    if (!pit) return;
    this.inPit = { kind: pit.kind, roll: pit.roll, t: round3(this.t) };
    this.metrics.record("fall_in", pit.kind, this.robot.x, this.robot.z, this.robot.speed, this.t);
    this.emit("fall_in", {
      what: pit.kind,
      wheels: pit.wheels,
      roll: pit.roll,
      speed: round3(this.robot.speed),
    });
    this.warn(
      pit.roll
        ? `Fell into the ${pit.kind.replace(/_/g, " ")} - rolled ${pit.roll}`
        : `Fell into the ${pit.kind.replace(/_/g, " ")}`,
    );
    this.robot.speed = 0;
    this.robot.yawRate = 0;
    this.metrics.breakMotion();
    // The attempt ends FALL_SETTLE_S from now, not this instant. Left running
    // forever the robot simply sat in the hole until the harness gave up — no
    // end panel, no report, nothing telling the player what had happened —
    // but ending it here instead froze the picture at the lip, before the
    // robot had tipped. "incomplete" is what it is: the delivery did not
    // happen and the robot is not going anywhere.
  }

  /**
   * Is the robot on the WRONG half of a two-way road, without an excuse?
   *
   * Traffic here keeps right, so on a two-way carriageway the route line runs
   * up the middle and the robot belongs on d >= 0 (kerbSide calls d < 0 the
   * left). Crossing to pass something is legal and normal — hence the same
   * clear-road test the nearside rule uses — and one-way roads have no wrong
   * half at all.
   */
  private onWrongSide(safety: SafetyResult): boolean {
    const net = this.level.world.network;
    if (!net || !this.level.world.path) return false;
    if (safety.freeDist < NEARSIDE_EXCUSE_M) return false; // passing something
    const piece = net.pieces[net.deepestPiece(this.robot.x, this.robot.z)];
    if (!piece || piece.kind !== "band" || piece.oneWay) return false;
    return this.frame.d < -WRONG_SIDE_MARGIN_M && this.robot.speed > 0.2;
  }

  /**
   * How far the body is from the NEARSIDE kerb (m).
   *
   * Marched outward rather than read from the distance field, because the
   * field gives the distance to the NEAREST kerb — which is the far one once
   * the robot has drifted across the road, and that is precisely the case
   * being measured. Traffic here keeps right, so the nearside is to the right
   * of travel.
   */
  private nearsideDistance(): number {
    const net = this.level.world.network;
    if (!net) return Infinity;
    // Right of travel: heading rotated +90 degrees.
    const rx = Math.cos(this.robot.heading);
    const rz = -Math.sin(this.robot.heading);
    for (let d = 0; d <= NEARSIDE_MARCH_M; d += NEARSIDE_MARCH_STEP) {
      if (net.sdf(this.robot.x + rx * d, this.robot.z + rz * d) >= 0) {
        return Math.max(0, d - this.shape.r);
      }
    }
    return NEARSIDE_MARCH_M;
  }

  /** Raise a transient on-screen warning; the HUD picks it up and clears it. */
  private warn(text: string): void {
    this.warning = { text, t: round3(this.t) };
  }

  /**
   * Is the robot crossing the kerb hard enough to get up it?
   *
   * Measured ACROSS the kerb line, not along it: brushing a kerb at a shallow
   * angle at speed should not launch the robot onto the pavement, and that is
   * exactly what a plain speed test would do.
   */
  private crossedKerbFast(): boolean {
    const net = this.level.world.network;
    if (!net) return false;
    const g = net.gradient(this.robot.x, this.robot.z); // outward unit normal
    const vx = this.robot.speed * Math.sin(this.robot.heading);
    const vz = this.robot.speed * Math.cos(this.robot.heading);
    const across = vx * g.x + vz * g.z;
    const needed = Math.max(KERB_MOUNT_MIN_MPS, KERB_MOUNT_ANGLE_SIN * this.eff.cruise_mps);
    return across >= needed;
  }

  /** Which safety semantics this attempt runs under. */
  private safetyOptions() {
    if (this.assistMode === "real") return REAL_SAFETY;
    return this.level.features.missionSafety ? MISSION_SAFETY : LAB_SAFETY;
  }

  /** Attempt timeout (s): the level's, or on the mission at least 1.5x the
   * route time at the effective cruise so a slow Tweak setting stays winnable. */
  timeoutS(): number {
    const path = this.level.world.path;
    if (!path) return this.level.timeoutS;
    return Math.max(this.level.timeoutS, (1.5 * path.length) / this.eff.cruise_mps);
  }

  /** Run until the attempt ends or maxSteps elapse (headless harness). */
  runToEnd(
    player?: (view: SimView, t: number) => JoystickCommand,
    maxSteps?: number,
  ): void {
    const cap = maxSteps ?? Math.ceil((this.timeoutS() + 5) / DT);
    for (let i = 0; i < cap && !this.ended; i++) {
      if (player) this.setInput(player(this.view(), this.t));
      this.step();
    }
  }

  /**
   * Stick-magnitude throttle (mission only): a light push crawls, a full push
   * runs. This is what a player reaches for when a corner arrives too fast —
   * before they think of releasing entirely. Lab levels return 1 so their
   * all-or-nothing cruise (and their trajectory pins) are untouched.
   */
  private throttle(mag: number): number {
    if (!this.level.features.frameControl) return 1;
    const t = clamp((mag - JOYSTICK_DEADZONE) / (1 - JOYSTICK_DEADZONE), 0, 1);
    return THROTTLE_MIN + (1 - THROTTLE_MIN) * t;
  }

  /**
   * Speed scale for the bend ahead: 1 on a straight, down to CORNER_MIN_SCALE
   * in the tightest turn. The robot slows itself INTO a corner the way a
   * driver does, so the player never has to fight the wheel at arcade pace
   * (and never needs a throttle, which would confound the research measure).
   */
  private cornerScale(): number {
    const path = this.level.world.path;
    if (!path) return 1;
    const look = Math.max(4, this.robot.speed * CORNER_LOOKAHEAD_S);
    const s0 = this.frame.s;
    const a = path.point(Math.min(path.length, s0 + 0.5));
    const b = path.point(Math.min(path.length, s0 + look));
    // Mean curvature over the lookahead (rad per metre).
    const curvature = Math.abs(wrapAngle(b.theta - a.theta)) / Math.max(1e-3, look);
    return Math.max(CORNER_MIN_SCALE, 1 / (1 + CORNER_GAIN * curvature * look));
  }

  /** Short post-encounter acceleration window (mission) or post-pass boost. */
  private isBoosting(): boolean {
    if (this.level.mission) return this.t < this.boostUntil;
    return this.boostActive || this.t < this.boostUntil;
  }

  // ------------------------------------------------------------------
  // Steering assistance (static geometry only — the pedestrian is NEVER
  // auto-dodged; pass-side choice must remain the player's).

  private assist(desiredRel: number): number {
    return this.assistWorld(this.worldHeading(desiredRel));
  }

  /** Assist around a WORLD heading (clamped to the command arc). */
  private assistWorld(desiredWorld: number): number {
    const desiredRel = clamp(wrapAngle(desiredWorld - this.frameYaw), -MAX_COMMAND_HEADING, MAX_COMMAND_HEADING);
    let bestHeading = this.worldHeading(desiredRel);
    let bestFree = -1;
    // A heading is "clear" when the robot could stop before the curb along
    // it: at arcade pace that is several meters, so the assist slides along
    // walls instead of aiming at them and letting the governor brake.
    const braking = (this.robot.speed * this.robot.speed) / (2 * this.eff.max_decel_mps2);
    const clear = this.level.features.frameControl
      ? Math.max(ASSIST_CLEAR_DIST, braking + STATIC_MARGIN + 0.3)
      : ASSIST_CLEAR_DIST;
    // A body already touching the curb measures zero along every heading;
    // probe from a point nudged just inside the road so sliding stays free.
    let probe: { x: number; z: number } = this.robot;
    const net = this.level.world.network;
    if (net && wallClearance(this.level.world, this.robot, this.shape) < 0.05) {
      const g = net.gradient(this.robot.x, this.robot.z);
      probe = { x: this.robot.x - g.x * 0.15, z: this.robot.z - g.z * 0.15 };
    }
    const offsets = this.level.features.frameControl ? ASSIST_OFFSETS_MISSION : ASSIST_OFFSETS;
    for (const off of offsets) {
      const h = this.worldHeading(desiredRel + off);
      const f = freeDistance(this.level.world, probe, this.robot.heading, h, this.shape, []);
      if (f >= clear) return h;
      if (f > bestFree) {
        bestFree = f;
        bestHeading = h;
      }
    }
    return bestHeading;
  }

  // ------------------------------------------------------------------
  // Stuck detection + automatic recovery.

  private watchForStuck(engaged: boolean): void {
    // Released (= yielding) or pivot-only steps RESTART the window: a stuck can
    // only trigger after a full STUCK_WINDOW_S of continuously engaged,
    // non-pivot input ("while input is active and not intentionally yielding").
    // Otherwise a normal pause-then-steer gesture would trip the watchdog on
    // the first engaged step, with the low-speed evidence carried over from
    // the pause — stealing control and invalidating research samples.
    if (!engaged || this.lastPivotOnly) {
      this.resetStuckWindow();
      return;
    }
    const sNow = this.progress();
    this.zHistory.push(sNow);
    if (this.zHistory.length > STUCK_WINDOW_STEPS + 1) this.zHistory.shift();

    if (this.t < this.stuckCooldownUntil) return;
    if (this.zHistory.length <= STUCK_WINDOW_STEPS) return;

    const progress = sNow - (this.zHistory[0] ?? sNow);
    const slow = Math.abs(this.robot.speed) < STUCK_SPEED_FRACTION * this.eff.cruise_mps;
    const noProgress = progress < STUCK_PROGRESS_FRACTION * this.robotProfile.length_m;
    const s = this.lastSafety;
    const staticBlocked = !!s && s.staticBound && s.freeDist < STUCK_FREE_DIST;
    const hazardNear = this.hazards().some(
      (hz) => Math.hypot(hz.x - this.robot.x, hz.z - this.robot.z) < STUCK_PED_NEAR_DIST,
    );

    // A robot standing at a corridor hold with the stick RELEASED is waiting,
    // not wedged (ADR-065). Without this, every patient yield at a pocket
    // read as stuck, and the recovery maneuver then aligned the robot toward
    // open space — sideways across the road it was about to drive down. A
    // pushing player is a different story (amendment F made pushing through
    // possible): if they push and still make no progress, that is a real
    // wedge and recovery must run.
    const corridorHolding =
      this.corridor !== null &&
      this.corridor.speedCap <= this.eff.cruise_mps * CORRIDOR_FLOOR_FRAC + 0.01 &&
      !(this.rawCmd.mag > 0.5 && this.rawCmd.dirY > 0.3);
    if (corridorHolding) return;
    if (!(slow && noProgress && staticBlocked && !hazardNear)) return;

    const consecutive =
      this.zAtLastRecoveryEnd !== null &&
      this.progress() - this.zAtLastRecoveryEnd < RECOVERY_CONSECUTIVE_PROGRESS;

    // "If recovery occurs during an encounter, record which encounter."
    const activeEncounter = this.encounterManager?.bump("recovery_events") ?? null;
    this.emit("stuck_detected", {
      consecutive,
      speed: round4(this.robot.speed),
      free_dist: s ? round4(s.freeDist) : null,
      progress: round4(progress),
      encounter_id: activeEncounter,
    });
    this.markRecoveryValidity();

    if (!consecutive) {
      this.recoveryMode = "reverse";
      this.reverseRemaining = RECOVERY_REVERSE_LENGTHS * this.robotProfile.length_m;
      this.packageCondition = Math.max(0, this.packageCondition - 8);
      this.emit("recovery_started", { kind: "reverse", encounter_id: activeEncounter });
    } else {
      this.packageCondition = Math.max(0, this.packageCondition - 12);
      this.restoreSafePose();
    }
    this.resetStuckWindow();
  }

  private recoveryStep(): void {
    if (this.recoveryMode === "reverse") {
      // Creep backward along the body axis (the one sanctioned reverse).
      this.robot.speed = Math.max(
        this.robot.speed - this.eff.max_decel_mps2 * DT,
        -RECOVERY_REVERSE_SPEED,
      );
      this.robot.yawRate = 0;
      this.robot.x += this.robot.speed * Math.sin(this.robot.heading) * DT;
      this.robot.z += this.robot.speed * Math.cos(this.robot.heading) * DT;
      clampToWalls(this.level.world, this.robot, this.shape);
      this.reverseRemaining -= Math.abs(this.robot.speed) * DT;

      const backFree = freeDistance(
        this.level.world,
        this.robot,
        this.robot.heading,
        wrapAngle(this.robot.heading + Math.PI),
        this.shape,
        this.hazards(),
      );
      if (this.reverseRemaining <= 0 || backFree < 0.15 || this.progress() <= 0) {
        // The maneuver stops here: speed and pose stay consistent (telemetry
        // must never show a nonzero speed against a frozen pose).
        this.robot.speed = 0;
        this.recoveryMode = "align";
        this.alignTarget = this.pickOpenHeading();
      }
      return;
    }

    // Align: rotate in place toward the open direction.
    const err = this.alignTarget - this.robot.heading;
    const desiredYaw = clamp(
      err * YAW_GAIN,
      -this.eff.max_yaw_rate_radps,
      this.eff.max_yaw_rate_radps,
    );
    const yawDelta = clamp(desiredYaw - this.robot.yawRate, -YAW_ACCEL * DT, YAW_ACCEL * DT);
    this.robot.yawRate = clamp(
      this.robot.yawRate + yawDelta,
      -this.eff.max_yaw_rate_radps,
      this.eff.max_yaw_rate_radps,
    );
    this.robot.heading = wrapAngle(this.robot.heading + this.robot.yawRate * DT);
    clampToWalls(this.level.world, this.robot, this.shape);

    if (Math.abs(wrapAngle(this.alignTarget - this.robot.heading)) < ALIGN_DONE_RAD) {
      this.robot.yawRate = 0;
      this.finishRecovery();
    }
  }

  private finishRecovery(): void {
    this.recoveryMode = "normal";
    this.recoveryCount += 1;
    this.zAtLastRecoveryEnd = this.progress();
    this.stuckCooldownUntil = this.t + STUCK_COOLDOWN_S;
    this.resetStuckWindow();
    this.emit("recovery_complete", {
      pose: [round4(this.robot.x), round4(this.robot.z)],
      heading: round4(this.robot.heading),
    });
  }

  private restoreSafePose(): void {
    const pose = { ...this.lastSafePose };
    // Never restore into any agent's current position.
    const path = this.level.world.path;
    for (const hz of this.hazards()) {
      if (Math.hypot(pose.x - hz.x, pose.z - hz.z) < 1.2) {
        if (path) {
          // Back the pose up along the delivery path, keeping its lateral offset.
          const f = path.project(pose.x, pose.z, this.frame.s);
          const back = Math.max(0, f.s - SAFE_POSE_PED_DIST);
          const p = path.offsetPoint(back, f.d);
          pose.x = p.x;
          pose.z = p.z;
          pose.heading = path.point(back).theta;
        } else {
          pose.z = Math.max(0, Math.min(pose.z, hz.z - SAFE_POSE_PED_DIST));
        }
      }
    }
    this.robot = { ...pose, speed: 0, yawRate: 0 };
    this.updateFrame(true);
    this.noProgressS = 0;
    this.penaltyTimeS += SAFE_POSE_PENALTY_S;
    this.recoveryCount += 1;
    this.zAtLastRecoveryEnd = this.progress();
    this.stuckCooldownUntil = this.t + STUCK_COOLDOWN_S;
    this.emit("safe_pose_restore", {
      penalty_s: SAFE_POSE_PENALTY_S,
      pose: [round4(pose.x), round4(pose.z)],
      heading: round4(pose.heading),
    });
  }

  /**
   * Where a pull-back should point the robot: back onto the delivery route.
   * Off the route it aims at the nearest route point (the way out); on the
   * route it is a deliberate U-turn, i.e. the reverse of the road direction.
   */
  private escapeHeading(): number {
    const path = this.level.world.path;
    if (!path) return wrapAngle(this.robot.heading + Math.PI);
    // ONE rule: point the robot back at its own route, a few metres ahead of
    // the progress it has actually made. On the route facing forward that is
    // where it is already pointing (a no-op); misaligned it straightens out;
    // past a junction or down a wrong branch it turns right around. The
    // player never has to know which case they are in.
    const p = path.point(Math.min(path.length, this.frame.s + 3));
    return Math.atan2(p.x - this.robot.x, p.z - this.robot.z);
  }


  /** Bearing of "onward": the path tangent (mission) or toward the marker (lab). */
  private onwardBearing(): number {
    if (this.level.world.path) return this.frame.theta;
    const d = this.level.world.destination;
    return Math.atan2(d.x - this.robot.x, d.z - this.robot.z);
  }

  private pickOpenHeading(): number {
    const bearingRel = clamp(
      wrapAngle(this.onwardBearing() - this.frameYaw),
      -MAX_COMMAND_HEADING,
      MAX_COMMAND_HEADING,
    );
    let best = this.worldHeading(bearingRel);
    let bestFree = -1;
    for (const off of ALIGN_OFFSETS) {
      const h = this.worldHeading(bearingRel + off);
      const f = freeDistance(this.level.world, this.robot, this.robot.heading, h, this.shape, []);
      if (f >= ALIGN_CLEAR_DIST) return h;
      if (f > bestFree) {
        bestFree = f;
        best = h;
      }
    }
    return best;
  }

  private trackSafePose(): void {
    if (this.t - this.lastSafePoseT < SAFE_POSE_INTERVAL_S) return;
    // A pose is unsafe while the safety governor is emergency-stopped.
    if (this.safetyState === "EMERGENCY_STOP") return;
    const world = this.level.world;
    const r = this.robot;

    if (wallClearance(world, r, this.shape) < SAFE_POSE_STATIC_MARGIN) return;

    const freeAhead = freeDistance(world, r, r.heading, r.heading, this.shape, []);
    if (freeAhead < 1.0) return;

    for (const hz of this.hazards()) {
      if (Math.hypot(hz.x - r.x, hz.z - r.z) < SAFE_POSE_PED_DIST) return;
    }

    const bearing = this.onwardBearing();
    if (Math.abs(wrapAngle(bearing - r.heading)) > SAFE_POSE_HEADING) return;

    this.lastSafePose = { x: r.x, z: r.z, heading: r.heading };
    this.lastSafePoseT = this.t;
  }

  private resetStuckWindow(): void {
    this.zHistory = [];
  }

  private markRecoveryValidity(): void {
    if (!this.researchValidity) return;
    // Mission: the reference commitment is the one made INSIDE the research
    // encounter; a recovery before it (including during earlier sections)
    // invalidates the primary sample. After it — or after the research
    // encounter completed — the decision data is retained and the episode is
    // marked "recovered".
    const rCfg = this.researchCfg();
    const rec = rCfg ? this.encounterManager?.record(rCfg.id) : null;
    const researchDone = rec
      ? rec.first_commitment !== null || rec.encounter_completion !== null
      : this.committedSide !== null;
    if (!researchDone) {
      this.researchValidity.sample_valid = false;
      this.researchValidity.invalid_reason = "recovery_before_commitment";
    } else {
      this.researchValidity.post_commit_recovery = true;
    }
  }

  // ------------------------------------------------------------------

  private stepPedestrian(): void {
    this.pedModel?.step({
      t: this.t,
      dt: this.dt,
      cueT: this.cueT,
      reactive: this.reactive,
      robot: this.robot,
      robotShape: this.shape,
    });
  }

  /** Step the lab pedestrian or all mission agents (+ abrupt-stop metric). */
  private stepActors(): void {
    if (this.agents.length === 0) {
      this.stepPedestrian();
      return;
    }
    const world = { network: this.level.world.network!, path: this.level.world.path! };
    const others = this.agents.map((x) => x.state);
    for (const a of this.agents) {
      if (!a.state.active) continue;
      a.step({
        t: this.t,
        dt: this.dt,
        robot: this.robot,
        robotShape: this.shape,
        world,
        robotFrame: this.frame,
        decision: this.encounterManager?.decisionFor(a.state.id) ?? null,
        cueT: a === (this.researchAgent as AgentModel | null) ? this.cueT : null,
        reactive: a === (this.researchAgent as AgentModel | null) ? this.reactive : false,
        obstacles: this.level.world.obstacles,
        others,
      });
      if (a.consumeAbruptStop()) {
        this.abruptCount += 1;
        this.emit("abrupt_agent", { agent_id: a.state.id, agent_type: a.state.type });
      }
    }
  }

  private processRawInputEvents(): void {
    const raw = this.rawCmd;
    const engaged = isEngaged(raw);

    if (engaged && !this.firstInputSeen) {
      this.firstInputSeen = true;
      this.emit("first_joystick_input", { dir: [raw.dirX, raw.dirY], mag: raw.mag });
    }
    if (
      engaged &&
      !this.firstLateralSeen &&
      Math.abs(raw.dirX) >= LATERAL_INPUT_THRESHOLD
    ) {
      this.firstLateralSeen = true;
      this.emit("first_lateral_input", {
        dir: [raw.dirX, raw.dirY],
        side: raw.dirX < 0 ? "left" : "right",
      });
    }
    if (!engaged && this.prevEngagedRaw && this.firstInputSeen) {
      this.emit("joystick_release", {});
    }
    this.prevEngagedRaw = engaged;

    // Dithering metric (score only): a sustained lateral push whose direction
    // flips within OSCILLATION_WINDOW_S of the previous one. Measured from the
    // raw stick, so route curvature can never manufacture it.
    const pushing = engaged && Math.abs(raw.dirX) >= LATERAL_INPUT_THRESHOLD;
    const side: Side | null = pushing ? (raw.dirX < 0 ? "left" : "right") : null;
    if (side !== null && side === this.pushSide) {
      this.pushHoldS += DT;
    } else {
      this.pushSide = side;
      this.pushHoldS = side === null ? 0 : DT;
    }
    if (side !== null && this.pushHoldS >= OSCILLATION_HOLD_S && side !== this.lastPushSide) {
      if (this.lastPushSide !== null && this.t - this.lastPushT <= OSCILLATION_WINDOW_S) {
        this.oscillationCount += 1;
      }
      this.lastPushSide = side;
      this.lastPushT = this.t;
    }
  }

  /** Keep trunks solid on every step, including the warning cooldown and recovery. */
  private blockTreeMotion(previous: { x: number; z: number; heading: number }): void {
    if (!this.level.mission || this.assistMode !== "real") return;
    const world = this.level.world;
    const trunks = world.obstacles.filter((ob) => ob.kind === "tree");
    if (!checkCollision({ ...world, obstacles: trunks }, this.robot, this.shape, []).collided) return;
    if (this.t >= this.collisionCooldownUntil) {
      this.emit("collision", { with: "obstacle", what: "tree", speed: round3(this.robot.speed) });
      this.metrics.record("collision", "tree", this.robot.x, this.robot.z, this.robot.speed, this.t);
      this.warn("Collision - tree");
      this.handleMissionCollision("obstacle");
    }
    // Reject just this step's penetrating pose, never a distant safe-pose teleport.
    this.robot.x = previous.x;
    this.robot.z = previous.z;
    this.robot.heading = previous.heading;
    this.robot.speed = 0;
    this.robot.yawRate = 0;
    this.metrics.breakMotion();
    this.onPavement = wallClearance(world, this.robot, this.shape) < 0;
  }

  private detect(): void {
    const world = this.level.world;
    // Preserve legacy lab agent handling while including actor-free custom maps.
    const isMission = this.level.mission !== undefined || this.agents.length > 0;

    // Collision: ends the attempt on lab levels; on the mission it costs
    // package condition + a safe-pose restore and the run continues.
    if (this.t >= this.collisionCooldownUntil) {
      const col = checkCollision(world, this.robot, this.shape, this.hazards());
      if (col.collided) {
        const what = col.obstacleKind ?? this.describeHit(col.with);
        this.emit("collision", { with: col.with, what, speed: round3(this.robot.speed) });
        this.metrics.record("collision", what, this.robot.x, this.robot.z, this.robot.speed, this.t);
        this.warn(`Collision - ${what}`);
        if (!isMission) {
          this.finish("collision");
          return;
        }
        this.handleMissionCollision(col.with);
      }
    }

    // Commitment latch + direction reversals (relative to the route
    // centerline; left = offset < 0). Suspended during automatic recovery —
    // an automated maneuver must never latch or flip a player decision.
    // first_commitment additionally requires engagement (ADR-010).
    if (this.recoveryMode === "normal") {
      const engaged = isEngaged(this.rawCmd);
      const off = this.offsetX();
      if (Math.abs(off) >= COMMIT_OFFSET) {
        const side: Side = off < 0 ? "left" : "right";
        if (this.committedSide === null) {
          if (engaged) {
            this.committedSide = side;
            this.lastSideAtCommitDepth = side;
            this.emit("first_commitment", { side, robot_x: this.robot.x, offset: off });
          }
        } else if (this.lastSideAtCommitDepth !== side) {
          this.lastSideAtCommitDepth = side;
          this.reversalCount += 1;
          this.emit("direction_reversal", { side, robot_x: this.robot.x, offset: off });
        }
      }
    }

    // Research protocol — the lab pedestrian, or the mission's section-2 agent.
    const researchPed = this.researchPedState();
    if ((this.level.isResearch || isMission) && researchPed) {
      const rCfg = this.researchCfg();
      const robotS = this.progress();
      const pedS = this.researchAgent ? this.researchAgent.s : researchPed.z;
      const pedOff = this.researchAgent ? this.researchAgent.lateral : researchPed.x;
      const inResearchZone =
        !isMission ||
        (rCfg !== null && robotS >= rCfg.zoneStart && robotS <= rCfg.zoneEnd) ||
        this.cueT !== null;
      const gap = pedS - robotS;

      if (inResearchZone && this.cueT === null && gap <= this.cueGap && gap > 0) {
        this.cueT = this.t;
        this.emit("cue_onset", {
          gap,
          variant: this.cfg.variant,
          mirror: this.cfg.mirror ? 1 : 0,
          pre_empted: this.reactive,
        });
        if (rCfg) this.encounterManager?.setResearchWindow(rCfg.id, this.t, null);
        this.advancePhase("cue");
      }

      if (!this.reactive) {
        // In the mission, only a commitment made INSIDE the research zone ends
        // the window (steering during warmup must not pre-empt the stimulus).
        const researchCommit =
          isMission && rCfg
            ? (this.encounterManager?.record(rCfg.id)?.commitment_direction ?? null)
            : this.committedSide;
        const byCommit = researchCommit !== null;
        // Proximity turns the pedestrian reactive even if no cue ever fired
        // (a robot stalled before the zone must not be walked into); window
        // bookkeeping only happens when a window actually existed.
        const byProximity = gap <= this.windowEndGap && gap > -1;
        if (byCommit || byProximity) {
          this.reactive = true;
          if (byCommit || this.cueT !== null) {
            this.emit("research_window_end", {
              reason: byCommit ? "commitment" : "proximity",
              gap,
            });
            if (rCfg) this.encounterManager?.setResearchWindow(rCfg.id, null, this.t);
            this.advancePhase("negotiation");
          }
        }
      }

      if (!this.crossed && robotS >= pedS) {
        this.crossed = true;
        this.passingT = this.t;
        const yielded = this.robot.speed < YIELD_SPEED;
        const robotOff = this.offsetX();
        this.passResult = yielded ? "yield" : robotOff < pedOff ? "pass_left" : "pass_right";
        this.emit("passing_moment", {
          result: this.passResult,
          robot_x: this.robot.x,
          ped_x: researchPed.x,
          robot_offset: round4(robotOff),
          ped_offset: round4(pedOff),
          lateral_gap: Math.abs(robotOff - pedOff),
          robot_speed: this.robot.speed,
        });
        this.advancePhase("completion");
      }

      // Post-pass boost once the pedestrian is safely behind. On the mission
      // this is a TIMED window like every other encounter reward (a permanent
      // latch would mask all later smooth/awkward feedback); the lab level
      // keeps its permanent boost for the short run to the marker.
      if (!this.boostActive && this.crossed && robotS - pedS > 1.5) {
        this.boostActive = true;
        if (isMission) this.boostUntil = Math.max(this.boostUntil, this.t + 1.8);
      }
    } else if (!this.level.isResearch && !isMission && this.robot.z > this.level.hazardZ) {
      this.advancePhase("completion");
    }

    // Mission encounter segmentation + feedback.
    if (this.encounterManager) {
      this.encounterManager.step(
        {
          t: this.t,
          robotX: this.robot.x,
          robotZ: this.robot.z,
          robotS: this.progress(),
          robotSpeed: this.robot.speed,
          robotOffset: this.offsetX(),
          engaged: isEngaged(this.rawCmd),
          joyX: this.rawCmd.dirX,
          cruise: this.eff.cruise_mps,
          recovering: this.recoveryMode !== "normal",
        },
        this.dt,
      );
      for (const ev of this.encounterManager.pending) {
        this.emit(ev.type, ev.data);
        if (ev.type === "encounter_start") {
          this.encounterHadCollision = false;
        } else {
          this.emitEncounterFeedback(ev.data["encounter_id"] as string);
        }
      }
      this.encounterManager.pending = [];
      this.updateGuidance();
    }

    // Destination reached.
    if (atDestination(world, this.robot, world.path ? this.frame : null)) {
      // The last encounter's zone ends at the customer: close it so every
      // started encounter has an encounter_end / completion time.
      if (this.encounterManager) {
        this.encounterManager.closeActive(this.t);
        for (const ev of this.encounterManager.pending) {
          this.emit(ev.type, ev.data);
          this.emitEncounterFeedback(ev.data["encounter_id"] as string);
        }
        this.encounterManager.pending = [];
      }
      this.emit("level_complete", { t: this.t });
      const result = this.level.isResearch ? (this.passResult ?? "completed") : "completed";
      this.finish(result, true);
      return;
    }

    // Timeout (mission: at least 1.5x the time the route takes at the effective cruise).
    if (this.t >= this.timeoutS()) {
      this.emit("timeout", {});
      this.finish("timeout");
    }
  }

  /** Turn-by-turn guidance + wrong-way detection (mission only). */
  private computeGuidance(): Guidance {
    const path = this.level.world.path!;
    const s = this.frame.s;
    if (this.needsTurnAround) {
      // Say the GESTURE, not just the problem: a lost player needs to know how.
      // And once they are heading back, say so — nagging "wrong way" at a
      // player who is already correcting is what made this confusing. The
      // gesture depends on the control model (ADR-023): the heading stick
      // turns the robot around when pulled back; the racing stick has no
      // pull-back — you hold the wheel over and it spins on the spot.
      const facingBack =
        Math.abs(wrapAngle(this.escapeHeading() - this.robot.heading)) < 0.5;
      const onTheWayBack = facingBack || this.closingOnRoute;
      const gesture =
        this.controlScheme === "rate"
          ? { wrong: "Wrong way — turn around", stalled: "Turn around — hold left or right" }
          : {
              wrong: "Wrong way — pull back to turn around",
              stalled: "Pull the stick back to turn around",
            };
      return {
        kind: "turn_around",
        distance: onTheWayBack ? Math.abs(this.frame.d) : null,
        exit: null,
        label: onTheWayBack
          ? "Head back to the route"
          : this.wrongWay
            ? gesture.wrong
            : gesture.stalled,
      };
    }
    const turn = path.nextTurn(s, 2);
    if (turn && turn.s - s <= GUIDE_DIST) {
      return {
        kind: turn.kind,
        distance: Math.max(0, turn.s - s),
        exit: turn.exit ?? null,
        label: turn.label,
      };
    }
    return { kind: "straight", distance: turn ? turn.s - s : null, exit: null, label: "Continue" };
  }

  private updateGuidance(): void {
    const path = this.level.world.path;
    if (!path) return;
    // Nothing to be wrong about: free roam has no errand, so "wrong way" is
    // just a driver looking at the other end of the street (ADR-128).
    if (this.freeRoam) {
      this.wrongWay = false;
      this.wrongWayTimer = 0;
      return;
    }
    // Off the corridor, or driving against the path direction, for a while.
    const net = this.level.world.network!;
    const allowed = ROUTE_PIECES[this.frame.piece] ?? [this.frame.piece];
    const onRoute = net.pieces.some(
      (p) =>
        (allowed.includes(p.id) || (p.kind === "band" && p.frame === false)) &&
        pieceSdf(p, this.robot.x, this.robot.z) < FRAME_INSET,
    );
    const off = !onRoute || Math.abs(this.frame.d) > this.frame.halfWidth + OFF_ROUTE_FAR;
    // Heading against the route. On its own this false-positives wherever
    // turning is expected — entering the roundabout flagged "wrong way" for
    // half a second while the player was doing exactly the right thing — so a
    // heading-only case must ALSO have lost real ground along the route over
    // the latch window (WRONG_WAY_BACK_M). Being off the route latches alone.
    const against =
      Math.abs(wrapAngle(this.robot.heading - this.frame.theta)) > (110 * Math.PI) / 180 &&
      this.robot.speed > 0.5;
    const bad = off || against;
    if (this.recoveryMode === "normal") {
      if (!this.wrongWay) {
        if (!bad) this.wrongWayTimer = 0;
        else {
          if (this.wrongWayTimer === 0) this.prevProgressS = this.frame.s;
          this.wrongWayTimer += this.dt;
        }
        const lostGround = this.prevProgressS - this.frame.s >= WRONG_WAY_BACK_M;
        if (this.wrongWayTimer >= WRONG_WAY_LATCH_S && (off || lostGround)) {
          this.wrongWay = true;
          this.wrongWayTimer = 0;
          this.wrongWayCount += 1;
          this.emit("wrong_way", { s: round3(this.frame.s), d: round3(this.frame.d), off, against });
        }
      } else {
        this.wrongWayTimer = !bad ? this.wrongWayTimer + this.dt : 0;
        if (this.wrongWayTimer >= WRONG_WAY_CLEAR_S) {
          this.wrongWay = false;
          this.wrongWayTimer = 0;
          this.emit("back_on_route", { s: round3(this.frame.s) });
        }
      }
    }
    // Progress bookkeeping: engaged and moving, but not getting closer.
    const toGo = Math.max(0, path.length - this.frame.s);
    const absD = Math.abs(this.frame.d);
    this.closingOnRoute = absD < this.prevAbsD - 1e-4;
    this.prevAbsD = absD;
    // Back in the corridor: forget the detour entirely, so the ordinary turn
    // guidance takes over again — including the turn that was missed, which
    // is now simply the next one ahead.
    const backInCorridor = onRoute && absD <= this.frame.halfWidth + 1.0;
    if (backInCorridor || toGo < this.bestToGo - 0.5) {
      this.bestToGo = Math.min(this.bestToGo, toGo);
      if (backInCorridor) this.bestToGo = toGo;
      this.noProgressS = 0;
    } else if (isEngaged(this.rawCmd) && this.recoveryMode === "normal") {
      this.noProgressS += this.dt;
    }
    this.needsTurnAround = this.wrongWay || this.noProgressS >= NO_PROGRESS_S;

    const g = this.computeGuidance();
    if (g.kind !== "straight" && g.kind !== "turn_around") {
      const turn = path.nextTurn(this.frame.s, 2);
      if (turn && this.shownTurnS !== turn.s) {
        this.shownTurnS = turn.s;
        this.emit("guidance_cue", {
          kind: g.kind,
          exit: g.exit,
          distance: round3(g.distance ?? 0),
          turn_s: round3(turn.s),
        });
      }
    }
    this.guidance = g;
  }

  /** Mission collisions never end the run: package hit + safe-pose restore. */
  /** Hitting the research pedestrian voids the sample, in either assist mode. */
  private invalidateOnResearchPed(withId: string | null): void {
    const researchPedId = this.researchAgent?.state.id;
    if (withId !== null && withId === researchPedId && this.researchValidity) {
      this.researchValidity.sample_valid = false;
      this.researchValidity.invalid_reason = "collision_with_research_pedestrian";
    }
  }

  /** A readable name for whatever was hit, for the warning and the report. */
  private describeHit(withId: string | null): string {
    if (withId === null) return "unknown";
    if (withId === "obstacle") return "obstacle";
    const agent = this.agents.find((a) => a.state.id === withId);
    return agent ? agent.state.type.replace(/_/g, " ") : withId.replace(/_/g, " ");
  }

  private handleMissionCollision(withId: string | null): void {
    this.collisionCount += 1;
    this.encounterHadCollision = true;
    if (this.assistMode === "real") {
      // Real physics: the impact stops the robot and nothing puts it back.
      // No package points come off, because points were the thing that could
      // not be checked; the hit is in the report with its time, place and
      // speed instead. Backing out is the player's job, as it is on the
      // hardware — the stuck watchdog is still there as a last resort.
      this.robot.speed = 0;
      this.robot.yawRate = 0;
      this.metrics.breakMotion();
      this.markRecoveryValidity();
      this.invalidateOnResearchPed(withId);
      this.collisionCooldownUntil = this.t + 0.4;
      return;
    }
    this.packageCondition = Math.max(0, this.packageCondition - 25);
    // A collision-triggered restore is a recovery for validity purposes too:
    // pre-research it invalidates the sample exactly like a stuck recovery.
    this.markRecoveryValidity();
    this.invalidateOnResearchPed(withId);
    this.encounterManager?.bump("recovery_events");

    // Hitting the SAME place again means the player is holding into it: a
    // safe-pose restore just feeds the loop (they drive straight back into
    // it). Back the robot out and turn it toward the way on instead — the
    // same maneuver the stuck watchdog uses, which the wider sliding assist
    // (amendment C) means a wall no longer triggers on its own.
    const here = Math.abs(this.progress() - this.collisionsHereS) < 4;
    this.collisionsHere = here ? this.collisionsHere + 1 : 1;
    this.collisionsHereS = this.progress();
    if (this.collisionsHere >= 2) {
      this.recoveryMode = "reverse";
      this.reverseRemaining = RECOVERY_REVERSE_LENGTHS * this.robotProfile.length_m;
      this.emit("recovery_started", { kind: "reverse", encounter_id: this.encounterManager?.active?.id ?? null });
      this.robot.speed = 0;
      this.metrics.breakMotion();
      this.collisionCooldownUntil = this.t + 2.5;
      return;
    }
    this.restoreSafePose();
    this.collisionCooldownUntil = this.t + 1.0;
  }

  /** Post-encounter feedback: smooth => tail wag + brief boost; awkward => wobble. */
  private emitEncounterFeedback(encounterId: string): void {
    const rec = this.encounterManager?.record(encounterId);
    if (!rec) return;
    // Zones with nothing to negotiate (warm-up props, junction turns) give no
    // feedback and no boost: a boost right before the research zone would
    // alter the stimulus timing.
    if (rec.encounter_type === "warmup_props" || rec.encounter_type === "junction_turn") return;
    const smooth =
      rec.emergency_stops === 0 &&
      rec.recovery_events === 0 &&
      !this.encounterHadCollision &&
      (rec.minimum_clearance === null || rec.minimum_clearance >= 0.12);
    if (smooth) {
      this.boostUntil = this.t + 1.8;
    } else {
      this.packageCondition = Math.max(0, this.packageCondition - 3);
    }
    this.emit("feedback", { kind: smooth ? "smooth" : "awkward", encounter_id: encounterId });
  }

  /** No destination, no timeout, no end: a reconstruction being looked at. */
  freeRoam = false;

  private advancePhase(next: Phase): void {
    if (PHASE_ORDER[next] > PHASE_ORDER[this.phase]) this.phase = next;
  }

  private finish(result: Outcome["result"], completed = false): void {
    // Free-roam has no errand, so it has no ending (ADR-101). Suppressing the
    // report in the UI was not enough: `ended` also stops the step loop, so the
    // robot froze wherever the route happened to finish and the street could
    // not be driven any further. The one exception is a genuine failure — a
    // collision or a fall still has to be reported, or the sim would silently
    // continue from inside a wall.
    if (this.freeRoam && result !== "collision") return;
    this.ended = true;
    this.outcome = {
      result,
      passing_moment_t: this.passingT,
      committed_side: this.committedSide,
      collision: result === "collision",
      completed,
      duration_s: this.t,
    };
    if (this.agents.length > 0) this.score = this.computeScore(completed);
    // The report is built for every level, not just the mission: a kerb climb
    // on cal2 is as checkable as one on the delivery route.
    this.report = this.metrics.finish(completed);
    // Final sample so the episode always ends with current state.
    this.samples.push(this.makeSample());
  }

  /** Delivery score — computed once, shown only after completion. */
  private computeScore(completed: boolean): MissionScore {
    const smoothness = Math.max(
      0,
      Math.min(
        100,
        100 -
          12 * this.estopCount -
          8 * this.recoveryCount -
          // Slowing for traffic is correct behaviour, not roughness: on a
          // district route it happens many times per run (ADR-022).
          1.5 * this.slowdownCount -
          4 * this.oscillationCount -
          15 * this.collisionCount,
      ),
    );
    // Time bonus scaled to the level's expected pace: full at 55 s, gone at 100 s.
    const timeBonus = completed ? Math.max(0, Math.min(20, (20 * (100 - this.t)) / 45)) : 0;
    const courtesy = Math.max(0, 10 - 3 * this.abruptCount);
    const navigation = 4 * this.wrongWayCount;
    const total = Math.round(
      Math.max(
        0,
        Math.min(
          100,
          (completed ? 30 : 0) +
            0.25 * this.packageCondition +
            0.25 * smoothness +
            timeBonus +
            courtesy -
            navigation,
        ),
      ),
    );
    return {
      completed,
      completion_time_s: round3(this.t),
      emergency_stops: this.estopCount,
      recovery_events: this.recoveryCount,
      collisions: this.collisionCount,
      direction_oscillations: this.oscillationCount,
      package_stability: this.packageCondition,
      smoothness,
      abrupt_agent_events: this.abruptCount,
      wrong_way_events: this.wrongWayCount,
      total,
    };
  }

  /**
   * The report as it stands right now, for a snapshot of an unfinished
   * attempt (the Download JSON button works mid-run). Does not latch:
   * finish() builds the final one.
   */
  reportSoFar(): RunReport {
    return this.metrics.finish(false);
  }

  /** Encounter records for the episode (mission only). */
  encounterRecords(): EncounterRecord[] | null {
    if (!this.encounterManager) return null;
    return this.encounterManager.finalize(this.researchValidity?.sample_valid ?? false);
  }

  /**
   * Recompute the corridor plan and turn its edges into events.
   *
   * The event edges live HERE rather than in the planner so the planner
   * stays a pure geometry module and everything observable about a run
   * flows through the sim's single event stream, like every other system.
   */
  private updateCorridor(): void {
    const world = this.level.world;
    const enabled = this.cfg.corridor ?? this.level.features.corridor;
    if (!enabled || !world.path || !world.network) return;
    this.corridorPlanner ??= new CorridorPlanner();
    const prev = this.corridor;
    const plan = this.corridorPlanner.plan({
      path: world.path,
      network: world.network,
      obstacles: world.obstacles,
      agents: this.agents.map((a) => a.state),
      robotS: this.frame.s,
      robotD: this.frame.d,
      robotX: this.robot.x,
      robotZ: this.robot.z,
      robotSpeed: this.robot.speed,
      robotHeading: this.robot.heading,
      shape: this.shape,
      decel: this.eff.max_decel_mps2,
      cruise: this.eff.cruise_mps,
      governor: this.safetyState,
      neutral: this.researchNeutral(),
      crossings: this.level.world.crossings,
      t: this.t,
      dt: DT,
    });
    this.corridor = plan;
    if (prev !== null) {
      if (plan.length < prev.length - 3.5 && this.t - this.lastShortenT > 1.5) {
        this.lastShortenT = this.t;
        this.emit("corridor_shortened", { state: plan.state, len: round3(plan.length) });
      }
      if (prev.pocket === null && plan.pocket !== null && this.t - this.lastPocketEventT > 2) {
        this.lastPocketEventT = this.t;
        this.pocketEntered = false;
        this.stoppedInYield = false;
        this.prematureFired = false;
        this.emit("pocket_created", {
          s0: round3(plan.pocket.s0),
          s1: round3(plan.pocket.s1),
          priority: plan.priorityId,
        });
      }
      if ((prev.amber === null || prev.amber.id !== plan.amber?.id) && plan.amber !== null) {
        // A NEW amber episode: the yield bookkeeping starts afresh, or a
        // second give-way could inherit the first one's 'stood still' credit.
        this.pocketEntered = false;
        this.stoppedInYield = false;
        this.prematureFired = false;
      }
      if (prev.amber !== null && plan.amber === null) {
        this.emit("amber_cleared", { id: prev.amber.id });
        // A resume only follows a wait: lean-only ambers (the band narrowed,
        // the robot never stopped for a pocket) must not fire resume_began
        // for a robot that was simply driving along.
        this.resumeArmed = this.pocketEntered || this.stoppedInYield;
        // A yield only counts when the robot was IN its pocket and truly
        // stood still — braking late in the middle of the road is not it.
        this.cleanYield = this.pocketEntered && this.stoppedInYield;
        if (this.cleanYield) this.emit("yield_success", { id: prev.amber.id });
      }
      if (prev.state === "RETURN_RIGHT" && plan.state === "OPEN_RIGHT") {
        this.emit("return_right_completed");
      }
    }
    if (plan.pocket !== null && !this.pocketEntered) {
      const inS = this.frame.s >= plan.pocket.s0 - 0.3 && this.frame.s <= plan.pocket.s1 + 0.3;
      const inD = this.frame.d >= plan.pocket.lo && this.frame.d <= plan.pocket.hi;
      if (inS && inD) {
        this.pocketEntered = true;
        this.emit("pocket_entered", { t_stop: null });
      }
    }
    if (plan.amber !== null && Math.abs(this.robot.speed) < 0.25) this.stoppedInYield = true;
    // Pushing forward at the corridor's mouth while the amber is active is
    // the player trying to go early — recorded, never punished on screen.
    if (
      plan.amber !== null &&
      plan.pocket !== null &&
      this.frame.s > plan.pocket.s1 - 1.4 &&
      this.rawCmd.mag > 0.5 &&
      this.rawCmd.dirY > 0.4
    ) {
      this.pushAtMouthS += DT;
      if (this.pushAtMouthS > 0.4 && !this.prematureFired) {
        this.prematureFired = true;
        this.emit("premature_resume");
      }
    } else {
      this.pushAtMouthS = 0;
    }
    if (this.resumeArmed && plan.amber === null && this.robot.speed > 0.5) {
      this.resumeArmed = false;
      this.emit("resume_began", { clean: this.cleanYield ? 1 : 0 });
    }
  }

  /**
   * True while the corridor must stay neutral for the research encounter:
   * inside the zone, plus a short approach so the band's right lean cannot
   * pre-position the robot before the stimulus starts.
   */
  private researchNeutral(): boolean {
    const cfg = this.researchCfg();
    if (!cfg) return false;
    return this.frame.s >= cfg.zoneStart - 6 && this.frame.s <= cfg.zoneEnd;
  }

  /**
   * Driving in the painted lane earns stars (amendment E): one per
   * LANE_STAR_EVERY_M of in-lane travel, combo growing with the streak and
   * dropped after a grace period off the lane. Paused through the research
   * window rather than reset — the window must stay feedback-free without
   * punishing the player for the study's needs.
   */
  private stepLaneReward(): void {
    const guide = this.corridorGuide;
    if (!guide || this.ended || this.recoveryMode !== "normal") return;
    if (this.researchNeutral()) return; // pause, keep streak
    const g = guideAt(guide, this.frame.s);
    // Generous capture: this is a reward, not an exam — near the lane counts.
    const inLane =
      g !== null &&
      this.frame.d >= g.lo - 0.35 &&
      this.frame.d <= g.hi + 0.2 &&
      this.robot.speed > 1.2;
    if (inLane) {
      this.offLaneS = 0;
      // Entering the lane rewards IMMEDIATELY (well, within about a metre —
      // instant firing strobed on the painted edge): the first star lands as
      // the robot settles onto the paint, then the cadence takes over.
      if (!this.wasInLane) {
        this.laneDistM = Math.max(this.laneDistM, LANE_STAR_EVERY_M - 1.2);
      }
      this.laneDistM += this.robot.speed * DT;
      if (this.laneDistM >= LANE_STAR_EVERY_M) {
        this.laneDistM -= LANE_STAR_EVERY_M;
        this.laneCombo = Math.min(99, this.laneCombo + 1);
        this.laneComboBest = Math.max(this.laneComboBest, this.laneCombo);
        this.laneStars += 1;
        const points = 10 * Math.min(this.laneCombo, 5);
        this.laneScore += points;
        this.emit("lane_star", { n: this.laneStars, combo: this.laneCombo, points });
      }
    } else {
      this.offLaneS += DT;
      if (this.offLaneS > LANE_COMBO_GRACE_S && this.laneCombo > 0) {
        this.laneCombo = 0;
        this.laneDistM = 0;
      }
    }
    this.wasInLane = inLane;
  }

  private makeSample(): SimSample {
    const ped = this.researchPedState();
    const active = ped?.active ?? false;
    const sample: SimSample = {
      t: round3(this.t),
      robot_pos: [round4(this.robot.x), round4(this.robot.z)],
      robot_heading: round4(this.robot.heading),
      robot_speed: round4(this.robot.speed),
      robot_yaw_rate: round4(this.robot.yawRate),
      robot_path: [round4(this.progress()), round4(this.offsetX())],
      ped_pos: active && ped ? [round4(ped.x), round4(ped.z)] : null,
      ped_vel: active && ped ? [round4(ped.vx), round4(ped.vz)] : null,
      joy_dir: [round4(this.rawCmd.dirX), round4(this.rawCmd.dirY)],
      joy_mag: round4(this.rawCmd.mag),
      safety_state: this.safetyState,
      phase: this.phase,
      variant: this.cfg.variant,
      mirror: this.cfg.mirror ? 1 : 0,
      seed: this.cfg.seed,
    };
    // Corridor levels append their fields; lab samples must stay verbatim
    // (their pinned trajectory hashes serialize the sample objects).
    const c = this.corridor;
    if (c !== null) {
      const iv = c.stations[0] ?? null;
      const gov = this.lastSafety ? this.lastSafety.speedCap : Infinity;
      sample.corridor_state = c.state;
      sample.corridor_len = round3(c.length);
      sample.corridor_width = round3(c.width);
      sample.corridor_center_d = round3(c.centerD);
      sample.corridor_pocket = c.pocket
        ? [round3(c.pocket.s0), round3(c.pocket.s1)]
        : null;
      sample.amber_id = c.amber?.id ?? null;
      sample.speed_cap = round3(Math.min(c.speedCap, Number.isFinite(gov) ? gov : c.speedCap));
      sample.conflict_dist = c.conflictS === null ? null : round3(c.conflictS);
      sample.priority_id = c.priorityId;
      sample.in_corridor =
        iv !== null && this.frame.d >= iv.lo - 0.05 && this.frame.d <= iv.hi + 0.05 ? 1 : 0;
    }
    // Driver-interface fields (ADR-075): mission levels only, for the same
    // reason as the corridor block — lab samples must serialize verbatim.
    if (this.level.mission) {
      sample.cmd = [round4(this.lastCmd.v), round4(this.lastCmd.w)];
      const near: NonNullable<SimSample["agents"]> = [];
      for (const a of this.agents) {
        const st = a.state;
        if (!st.active) continue;
        const dx = st.x - this.robot.x;
        const dz = st.z - this.robot.z;
        if (dx * dx + dz * dz > 60 * 60) continue;
        near.push([
          st.id,
          st.type,
          round3(st.x),
          round3(st.z),
          round3(st.vx),
          round3(st.vz),
          round4(st.heading),
          round3(st.radius),
        ]);
      }
      sample.agents = near;
    }
    return sample;
  }

  private emit(type: SimEvent["type"], data?: Record<string, unknown>): void {
    this.events.push({ t: round3(this.t), type, data });
  }
}

function round3(v: number): number {
  return Math.round(v * 1000) / 1000;
}
function round4(v: number): number {
  return Math.round(v * 10000) / 10000;
}
function finite(v: number): number | null {
  return Number.isFinite(v) ? round3(v) : null;
}
